import { FC, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import type { Feature } from "@services/permissions/commands/cmdCreateFeature";
import FeatureFormModal from "./FeatureFormModal";
import ModalDelete from "../../../components/common/ModalDelete";

interface FlatFeature {
  feature: Feature;
  depth: number;
}

function flattenTree(features: Feature[]): FlatFeature[] {
  const childrenMap = new Map<number | null, Feature[]>();
  for (const f of features) {
    const key = f.parent_id ?? null;
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key)!.push(f);
  }
  const result: FlatFeature[] = [];
  function walk(parentId: number | null, depth: number) {
    const children = childrenMap.get(parentId) || [];
    for (const child of children) {
      result.push({ feature: child, depth });
      walk(child.id, depth + 1);
    }
  }
  walk(null, 0);
  return result;
}

interface Props {
  features: Feature[];
  isLoading: boolean;
  onCreate: (name: string, parentId: number | null) => Promise<void>;
  onUpdate: (id: number, data: { name?: string; parent_id?: number | null }) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

const FeaturesSection: FC<Props> = ({ features, isLoading, onCreate, onUpdate, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const flat = flattenTree(features);

  const handleSave = async (data: { name: string; parent_id: number | null }) => {
    setIsSaving(true);
    try {
      if (editingFeature) await onUpdate(editingFeature.id, data);
      else await onCreate(data.name, data.parent_id);
      setShowModal(false);
      setEditingFeature(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsSaving(true);
    try {
      await onDelete(deleteTarget.id);
    } finally {
      setIsSaving(false);
      setDeleteTarget(null);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="features-table">
        <div className="features-table__header">
          <span>Name</span>
          <Button
            variant="primary"
            size="sm"
            onClick={() => { setEditingFeature(null); setShowModal(true); }}
          >
            <i className="bi bi-plus-circle me-1" />New Feature
          </Button>
        </div>
        <div className="features-table__body">
          {flat.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <p className="mb-0">No features yet. Create one to get started.</p>
            </div>
          ) : (
            flat.map(({ feature, depth }) => (
              <div key={feature.id} className="features-table__row">
                <span
                  className="features-table__name"
                  style={{ paddingLeft: depth * 24 }}
                >
                  {depth > 0 && <span className="features-table__indent-guide">└</span>}
                  {feature.name}
                </span>
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="link"
                    size="sm"
                    className="features-table__kebab"
                  >
                    <i className="bi bi-three-dots-vertical" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
                    <Dropdown.Item onClick={() => { setEditingFeature(feature); setShowModal(true); }}>
                      <i className="bi bi-pencil me-2" />Edit
                    </Dropdown.Item>
                    <Dropdown.Item
                      className="text-danger"
                      onClick={() => setDeleteTarget({ id: feature.id, name: feature.name })}
                    >
                      <i className="bi bi-trash me-2" />Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            ))
          )}
        </div>
      </div>

      <FeatureFormModal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingFeature(null); }}
        onSave={handleSave}
        feature={editingFeature}
        allFeatures={features}
        isSaving={isSaving}
      />

      <ModalDelete
        title="Feature"
        objectLabel={deleteTarget?.name || ""}
        deleteFn={handleDelete}
        visible={deleteTarget !== null}
        toggleVisibility={(v: boolean) => { if (!v) setDeleteTarget(null); }}
        isLoading={isSaving}
      />
    </>
  );
};

export default FeaturesSection;
